import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { LayerProps, MapRef, VectorTileSource } from 'react-map-gl/maplibre'
import { APISignalementTiles } from '../config/map/layers'

interface MapContextValue {
  mapRef: MapRef | null
  mapRefCb: (instance: MapRef | null) => void
  mapChildren: React.ReactNode | null
  setMapChildren: (children: React.ReactNode) => void
  showCadastre: boolean
  setShowCadastre: React.Dispatch<React.SetStateAction<boolean>>
  editParcelles: boolean
  setEditParcelles: React.Dispatch<React.SetStateAction<boolean>>
  adresseSearchMapLayersOptions: Record<string, Partial<LayerProps>>
  setAdresseSearchMapLayersOptions: React.Dispatch<
    React.SetStateAction<Record<string, Partial<LayerProps>>>
  >
  signalementSearchMapLayerOptions: Partial<LayerProps>
  setSignalementSearchMapLayerOptions: React.Dispatch<React.SetStateAction<Partial<LayerProps>>>
  reloadAPISignalementTiles: () => void
  mapMessage: string | null
  setMapMessage: React.Dispatch<React.SetStateAction<string | null>>
}

export const MapContext = createContext<MapContextValue>({
  mapRef: null,
  mapRefCb: () => {},
  mapChildren: null,
  setMapChildren: () => {},
  showCadastre: false,
  setShowCadastre: () => {},
  editParcelles: false,
  setEditParcelles: () => {},
  adresseSearchMapLayersOptions: {
    adresse: {},
    'adresse-label': {},
    voie: {},
    toponyme: {},
  },
  setAdresseSearchMapLayersOptions: () => {},
  signalementSearchMapLayerOptions: {},
  setSignalementSearchMapLayerOptions: () => {},
  reloadAPISignalementTiles: () => {},
  mapMessage: null,
  setMapMessage: () => {},
})

export function MapContextProvider(props: { children: React.ReactNode }) {
  const [mapRef, setMapRef] = useState<MapRef | null>(null)
  const mapRefCb = useCallback((node: MapRef | null) => {
    if (node !== null) {
      setMapRef(node)
    }
  }, [])
  const [mapChildren, setMapChildren] = useState<React.ReactNode>(null)
  const [showCadastre, setShowCadastre] = useState(false)
  const [editParcelles, setEditParcelles] = useState(false)
  const [adresseSearchMapLayersOptions, setAdresseSearchMapLayersOptions] = useState<
    Record<string, Partial<LayerProps>>
  >({
    adresse: {},
    'adresse-label': {},
    voie: {},
    toponyme: {},
  })
  const [signalementSearchMapLayerOptions, setSignalementSearchMapLayerOptions] = useState<
    Partial<LayerProps>
  >({})
  const [mapMessage, setMapMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!showCadastre && editParcelles) {
      setEditParcelles(false)
    }
  }, [showCadastre, editParcelles])

  const reloadAPISignalementTiles = useCallback(() => {
    if (mapRef) {
      const source: VectorTileSource = mapRef.getSource('api-signalement') as VectorTileSource
      source.setTiles(APISignalementTiles)
    }
  }, [mapRef])

  const value = useMemo(
    () => ({
      mapRef,
      mapRefCb,
      mapChildren,
      setMapChildren,
      showCadastre,
      setShowCadastre,
      editParcelles,
      setEditParcelles,
      adresseSearchMapLayersOptions,
      setAdresseSearchMapLayersOptions,
      signalementSearchMapLayerOptions,
      setSignalementSearchMapLayerOptions,
      reloadAPISignalementTiles,
      mapMessage,
      setMapMessage,
    }),
    [
      mapRef,
      mapRefCb,
      mapChildren,
      setMapChildren,
      showCadastre,
      setShowCadastre,
      editParcelles,
      setEditParcelles,
      adresseSearchMapLayersOptions,
      setAdresseSearchMapLayersOptions,
      signalementSearchMapLayerOptions,
      setSignalementSearchMapLayerOptions,
      reloadAPISignalementTiles,
      mapMessage,
      setMapMessage,
    ],
  )

  return <MapContext.Provider value={value} {...props} />
}

export default MapContext
